import { initializeApp } from 'firebase/app'
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth'

// Firebase config is provided at build time via VITE_FIREBASE_* env vars.
// In production these are baked into the bundle by Vite; in dev they come
// from .env.local. The values are not secret — the API key is a client
// identifier, not an authentication credential (auth happens via Identity
// Platform's OAuth/email flows, not via the API key itself).
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
}

// Fail fast on a misconfigured build (FE-6). A missing VITE_FIREBASE_* var
// otherwise produces a confusing downstream error deep inside the Firebase
// SDK (e.g. "auth/invalid-api-key") at first signin; surfacing it here, at
// module load, lets the app's ErrorBoundary show an actionable message
// instead. These values are not secret — they only identify the project.
const missing = (
  [
    ['VITE_FIREBASE_API_KEY', firebaseConfig.apiKey],
    ['VITE_FIREBASE_AUTH_DOMAIN', firebaseConfig.authDomain],
    ['VITE_FIREBASE_PROJECT_ID', firebaseConfig.projectId],
  ] as const
)
  .filter(([, v]) => !v)
  .map(([k]) => k)
if (missing.length > 0) {
  throw new Error(
    `Firebase is misconfigured: missing ${missing.join(', ')}. ` +
      'Set these VITE_FIREBASE_* environment variables at build time ' +
      '(see .env.local / the deploy config).',
  )
}

export const firebaseApp = initializeApp(firebaseConfig)
export const auth = getAuth(firebaseApp)

// Wire up the Firebase Auth emulator when the URL is set. Pairs with the
// backend's FIREBASE_AUTH_EMULATOR_HOST env var so both sides verify
// against the same emulator instance (see internal/identity/firebase.go).
const emulatorUrl = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL
if (emulatorUrl) {
  connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
}

// Provider singletons reused across signin calls. Setting them here keeps
// scope-customisation in one place if we add hosted-domain hints or extra
// scopes later.
export const googleProvider = new GoogleAuthProvider()
export const microsoftProvider = new OAuthProvider('microsoft.com')
